import React, { useState, useEffect, useContext, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "react-toastify";
import axios from "axios";
import { UserContext } from '../../contexts/UserContext';
import { ChevronDown } from 'lucide-react';
import TrainingMailSender from './TrainingMailSender';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "") || ""}/api`;

// Helper functions for date handling with timezone
// IMPORTANT: Django sends dates in UTC with timezone info (e.g., "2024-01-15T10:00:00Z")
// and we need to display them correctly in user's local timezone
const formatDateTimeForInput = (dateString) => {
  if (!dateString) return '';
  
  // Create date object from the string (Django sends dates with timezone info)
  // This automatically converts UTC to local timezone
  const date = new Date(dateString);
  
  // Format for datetime-local input (YYYY-MM-DDTHH:MM)
  // This will show the date in user's local timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatDateTimeForBackend = (dateTimeLocalString) => {
  if (!dateTimeLocalString) return '';
  
  // datetime-local input gives us YYYY-MM-DDTHH:MM in user's local timezone
  // We need to send it as ISO string which will include timezone info
  const date = new Date(dateTimeLocalString);
  return date.toISOString();
};

const formatDateTimeForDisplay = (dateString) => {
  if (!dateString) return 'Not set';
  return new Date(dateString).toLocaleString();
};

// Helper function to add hours to a datetime-local string
const addHoursToDateTimeLocal = (dateTimeLocalString, hours) => {
  if (!dateTimeLocalString) return '';
  
  const date = new Date(dateTimeLocalString);
  date.setHours(date.getHours() + hours);
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hoursStr = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hoursStr}:${minutes}`;
};

// Component for creating/editing training with new Training model
const TrainingEditModal = ({ isOpen, onClose, trainingData, fetchData }) => {
  
  const { user } = useContext(UserContext);
  const hasUpdatedUsersRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [trainingCenterQualifications, setTrainingCenterQualifications] = useState([]);
  const [qualificationFilter, setQualificationFilter] = useState('');
  const [showQualificationDropdown, setShowQualificationDropdown] = useState(false);
  const [showUserPanel, setShowUserPanel] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userFilter, setUserFilter] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [trainingRoles, setTrainingRoles] = useState([]);
  const [trainingSites, setTrainingSites] = useState([]);
  const [trainingCountries, setTrainingCountries] = useState([]);
  const [managers, setManagers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [mailSenderData, setMailSenderData] = useState(null);
  const [originalData, setOriginalData] = useState(null); // Store original data for change detection
  const [dateValidationError, setDateValidationError] = useState('');
  const [filters, setFilters] = useState({
    trainingRoles: [],
    trainingSites: [],
    trainingCountries: [],
    managers: [],
    supervisors: [],
    coordinators: [],
    qualificationStatus: []
  });
  const [dropdownStates, setDropdownStates] = useState({
    roles: false,
    sites: false,
    countries: false,
    managers: false,
    supervisors: false,
    coordinators: false,
    qualificationStatus: false
  });
  const [searchTerms, setSearchTerms] = useState({
    roles: '',
    sites: '',
    countries: '',
    managers: '',
    supervisors: '',
    coordinators: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    training_number_filter: '',
    training_center_qualification_id: '',
    start_date: '',
    end_date: '',
    description: '',
    required_items: '',
    availability: '',
    status: 'planned'
  });

  const isEditing = trainingData && !trainingData.isNew;

  useEffect(() => {
    if (isOpen) {
      // Reset the users update ref for each modal opening
      hasUpdatedUsersRef.current = false;
      if (isEditing) {
        // Load existing training data
        // Extract proper training_center_qualification_id
        let qualificationId = '';
        if (trainingData.training_center_qualification_id) {
          qualificationId = trainingData.training_center_qualification_id;
        } else if (trainingData.training_center_qualification) {
          qualificationId = trainingData.training_center_qualification;
        }

        const editingFormData = {
          name: trainingData.name || '',
          training_number_filter: '',
          training_center_qualification_id: qualificationId,
          start_date: formatDateTimeForInput(trainingData.start_date),
          end_date: formatDateTimeForInput(trainingData.end_date),
          description: trainingData.description || '',
          required_items: trainingData.required_items || '',
          availability: trainingData.availability || '',
          status: trainingData.status || 'planned'
        };
        
        setFormData(editingFormData);
        
        // Load existing participants - users_data can be full objects or just IDs (from training-calendar-data)
        let usersList = [];
        let participantIds = [];

        if (trainingData.users_data && Array.isArray(trainingData.users_data)) {
          // Check if users_data contains full objects or just IDs
          if (trainingData.users_data.length > 0) {
            if (typeof trainingData.users_data[0] === 'object') {
              // Full user objects
              usersList = trainingData.users_data;
              participantIds = usersList.map(u => u.id);
            } else {
              // Just IDs - create placeholder objects that will be populated when users are fetched
              participantIds = trainingData.users_data;
              usersList = participantIds.map(id => ({ id, _placeholder: true }));
            }
          }
        }

        // Store original data for change detection (only for approved trainings)
        if (trainingData.status === 'approved') {
          setOriginalData({
            training_center_qualification_id: editingFormData.training_center_qualification_id ? editingFormData.training_center_qualification_id.toString() : '',
            start_date: editingFormData.start_date,
            end_date: editingFormData.end_date,
            description: editingFormData.description,
            required_items: editingFormData.required_items,
            participants: participantIds // Store original participant IDs
          });
        }

        if (usersList.length > 0) {
          setSelectedUsers(usersList);
          // Auto-show user panel if there are existing participants
          setShowUserPanel(true);
          fetchUsers(); // Load users data when panel opens automatically
        }
      } else {
        // Reset for new training
        // Use dates from trainingData if provided (e.g., from UserDayView), otherwise default to tomorrow
        let startDate, endDate;

        if (trainingData?.start_date) {
          // Use the date passed from the parent component (e.g., UserDayView)
          startDate = trainingData.start_date;
          endDate = trainingData.end_date || trainingData.start_date;
        } else {
          // Default to tomorrow if no date is provided
          const now = new Date();
          const tomorrow = new Date(now);
          tomorrow.setDate(now.getDate() + 1);
          startDate = tomorrow.toISOString();
          endDate = tomorrow.toISOString();
        }

        setFormData({
          name: '',
          training_number_filter: trainingData?.trainingNumber || '',
          training_center_qualification_id: '',
          start_date: formatDateTimeForInput(startDate),
          end_date: formatDateTimeForInput(endDate),
          description: trainingData?.description || '',
          required_items: '',
          availability: '',
          status: 'planned'
        });
        
        // Set selected users from CertificateExp
        if (trainingData?.users && Array.isArray(trainingData.users)) {
          setShowUserPanel(true);
          fetchUsers(); // Load users data when panel opens automatically
        } else {
          setSelectedUsers([]);
        }
      }
      fetchTrainingCenterQualifications();
    }
  }, [isOpen, trainingData, isEditing]);

  // Update filter text when qualifications are loaded and form data is set
  useEffect(() => {
    console.log('useEffect triggered:', {
      qualification_id: formData.training_center_qualification_id,
      qualifications_length: trainingCenterQualifications.length,
      qualifications: trainingCenterQualifications
    });
    
    if (formData.training_center_qualification_id && trainingCenterQualifications.length > 0) {
      const selectedQual = trainingCenterQualifications.find(
        qual => qual.id === parseInt(formData.training_center_qualification_id) || qual.id.toString() === formData.training_center_qualification_id.toString()
      );
      console.log('Found selected qualification:', selectedQual);
      
      if (selectedQual) {
        const qualName = selectedQual.qualification_type?.qualification_type_data?.name || 'Unknown';
        const centerName = selectedQual.training_center?.name || 'Unknown';
        const trainingNumber = selectedQual.qualification_type?.training_number || '';
        const price = selectedQual.price ? ` - ${selectedQual.price}` : '';
        const displayName = `${qualName} - ${centerName}${trainingNumber ? ` (${trainingNumber})` : ''}${price}`;
        console.log('Setting qualification filter to:', displayName);
        setQualificationFilter(displayName);
      } else {
        console.log('No matching qualification found for ID:', formData.training_center_qualification_id);
      }
    } else if (!formData.training_center_qualification_id) {
      console.log('Clearing qualification filter');
      setQualificationFilter('');
    }
  }, [formData.training_center_qualification_id, trainingCenterQualifications]);

  // Map user IDs to full user objects when users data is loaded
  useEffect(() => {
    if (trainingData && !isEditing && trainingData.users && Array.isArray(trainingData.users) && users.length > 0) {
      console.log('Mapping user IDs to full objects:', {
        trainingDataUsers: trainingData.users,
        loadedUsers: users.length
      });
      
      const selectedUserData = trainingData.users.map(userItem => {
        if (typeof userItem === 'object' && userItem.id) {
          // Already a user object
          return userItem;
        } else {
          // It's a user ID, find the full user object
          const foundUser = users.find(u => u.id === userItem);
          if (foundUser) {
            console.log('Found user for ID', userItem, ':', foundUser.first_name, foundUser.last_name);
            return foundUser;
          } else {
            console.warn('User not found for ID:', userItem);
            return { id: userItem, first_name: 'Unknown', last_name: 'User', username: userItem.toString() };
          }
        }
      });
      
      console.log('Setting selected users:', selectedUserData);
      setSelectedUsers(selectedUserData);
    }
    
    // For editing mode, update selectedUsers with full user data once users are fetched
    if (isEditing && users.length > 0 && selectedUsers.length > 0 && !hasUpdatedUsersRef.current) {
      console.log('Updating selected users with full data for editing mode:', {
        currentSelectedUsers: selectedUsers,
        availableUsers: users.length
      });

      const updatedSelectedUsers = selectedUsers.map(selectedUser => {
        // Find the full user object from the fetched users
        const fullUser = users.find(u => u.id === selectedUser.id);
        if (fullUser) {
          console.log('Updated user data for:', selectedUser.id, 'Role:', fullUser.training_role_data?.name);
          return fullUser;
        }
        // If not found in fetched users, keep the original
        console.warn('Could not find full data for user:', selectedUser.id);
        return selectedUser;
      });

      // Check if we need to update - either placeholders or missing role data
      const hasImprovements = updatedSelectedUsers.some((user, index) =>
        selectedUsers[index]._placeholder ||
        (user.training_role_data && !selectedUsers[index].training_role_data) ||
        (user.first_name && !selectedUsers[index].first_name)
      );

      if (hasImprovements) {
        console.log('Updating selected users with improved data:', updatedSelectedUsers);
        setSelectedUsers(updatedSelectedUsers);
        hasUpdatedUsersRef.current = true;
      }
    }
  }, [users, trainingData, isEditing]);

  // Auto-select training center qualification when training_number_filter is set from CertificateExp
  useEffect(() => {
    if (formData.training_number_filter && trainingCenterQualifications.length > 0 && !formData.training_center_qualification_id) {
      const matchingQualifications = trainingCenterQualifications.filter(qual => 
        qual.qualification_type?.training_number === formData.training_number_filter
      );
      
      console.log('Auto-selecting qualifications for training number:', formData.training_number_filter, 'Found:', matchingQualifications);
      
      if (matchingQualifications.length === 1) {
        // Jeśli jest tylko jedna opcja, ustaw ją automatycznie
        const qual = matchingQualifications[0];
        setFormData(prev => ({ 
          ...prev, 
          training_center_qualification_id: qual.id.toString(),
          description: qual.description || '',
          required_items: qual.required_items || ''
        }));
        setQualificationFilter(getQualificationDisplayName(qual));
        console.log('Auto-selected single qualification:', qual);
      }
      // Jeśli więcej niż jedna opcja, nie robimy nic - user sam wybierze
    }
  }, [formData.training_number_filter, trainingCenterQualifications, formData.training_center_qualification_id]);

  // Auto-select training center qualification when qualificationType ID is provided from UserDayView
  useEffect(() => {
    console.log('TrainingEditModal: checking qualificationType from UserDayView:', trainingData?.qualificationType);
    
    if (trainingData?.qualificationType?.id && trainingCenterQualifications.length > 0 && !formData.training_center_qualification_id && !formData.training_number_filter) {
      // Find matching qualifications based on qualification_type.qualification_type_data.id
      const matchingQualifications = trainingCenterQualifications.filter(qual => {
        const matches = qual.qualification_type?.qualification_type_data?.id === trainingData.qualificationType.id;
        console.log('Checking qual:', qual.qualification_type?.qualification_type_data?.id, 'against:', trainingData.qualificationType.id, 'matches:', matches);
        return matches;
      });
      
      console.log('Auto-selecting qualifications for qualification type ID:', trainingData.qualificationType.id, 'Found:', matchingQualifications);
      
      if (matchingQualifications.length > 0) {
        // Get training number from the first match (they should all have the same training_number)
        const trainingNumber = matchingQualifications[0].qualification_type?.training_number || '';
        console.log('Found training number:', trainingNumber);
        
        // Set training_number_filter which will trigger the existing auto-selection logic
        setFormData(prev => ({ 
          ...prev, 
          training_number_filter: trainingNumber
        }));
        
        if (matchingQualifications.length === 1) {
          console.log('Single qualification found, will be auto-selected by training_number_filter logic');
        } else {
          console.log('Multiple qualifications found, user can choose from filtered list');
        }
      }
    }
  }, [trainingData?.qualificationType?.id, trainingCenterQualifications, formData.training_center_qualification_id, formData.training_number_filter]);

  // Debug effect to track trainingData changes
  useEffect(() => {
    if (trainingData?.isMultipleTrainings) {
      console.log('TrainingEditModal: trainingData updated for multiple trainings:', trainingData);
    }
  }, [trainingData]);

  const fetchTrainingCenterQualifications = async () => {
    console.log('Fetching training center qualifications...');
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API_BASE_URL}/training-center-qualifications/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Training Center Qualifications fetched:", response.data);
      console.log("Current formData when fetching:", formData);
      setTrainingCenterQualifications(response.data || []);
      console.log("Training Center Qualifications data structure:", response.data);
      console.log("First qualification object:", response.data?.[0]);
    } catch (error) {
      console.error("Error fetching training center qualifications:", error);
    }
  };

  const fetchUsers = async () => {
    console.log('Fetching users...');
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API_BASE_URL}/users/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Users data fetched:", response.data);
      console.log("Users data structure:", response.data);
      console.log("First user object:", response.data?.[0]);
      console.log("Total users count:", response.data?.length);
      setUsers(response.data || []);
      
      // Extract unique data for filters
      const roles = response.data?.reduce((acc, user) => {
        if (user.training_role_data && !acc.find(role => role.id === user.training_role_data.id)) {
          acc.push({
            id: user.training_role_data.id,
            name: user.training_role_data.name
          });
        }
        return acc;
      }, []) || [];
      
      const sites = response.data?.reduce((acc, user) => {
        if (user.training_site_data && !acc.find(site => site.id === user.training_site_data.id)) {
          acc.push({
            id: user.training_site_data.id,
            name: user.training_site_data.name
          });
        }
        return acc;
      }, []) || [];
      
      const countries = response.data?.reduce((acc, user) => {
        if (user.training_country_data && !acc.find(country => country.id === user.training_country_data.id)) {
          acc.push({
            id: user.training_country_data.id,
            name: user.training_country_data.name
          });
        }
        return acc;
      }, []) || [];
      
      const managersList = response.data?.reduce((acc, user) => {
        if (user.manager_data && !acc.find(manager => manager.id === user.manager_data.id)) {
          acc.push({
            id: user.manager_data.id,
            first_name: user.manager_data.first_name,
            last_name: user.manager_data.last_name
          });
        }
        return acc;
      }, []) || [];
      
      const supervisorsList = response.data?.reduce((acc, user) => {
        if (user.training_supervisor_data && !acc.find(supervisor => supervisor.id === user.training_supervisor_data.id)) {
          acc.push({
            id: user.training_supervisor_data.id,
            first_name: user.training_supervisor_data.first_name,
            last_name: user.training_supervisor_data.last_name
          });
        }
        return acc;
      }, []) || [];
      
      const coordinatorsList = response.data?.reduce((acc, user) => {
        if (user.training_coordinator_data && !acc.find(coordinator => coordinator.id === user.training_coordinator_data.id)) {
          acc.push({
            id: user.training_coordinator_data.id,
            first_name: user.training_coordinator_data.first_name,
            last_name: user.training_coordinator_data.last_name
          });
        }
        return acc;
      }, []) || [];
      
      setTrainingRoles(roles);
      setTrainingSites(sites);
      setTrainingCountries(countries);
      setManagers(managersList);
      setSupervisors(supervisorsList);
      setCoordinators(coordinatorsList);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleInputChange = (field, value) => {
    // Special handling for availability field
    if (field === 'availability') {
      const newAvailability = value ? parseInt(value) : null;
      
      // Check if new availability is less than currently selected users
      if (newAvailability && selectedUsers.length > newAvailability) {
        toast.warning(`Cannot set availability to ${newAvailability}. You have already selected ${selectedUsers.length} participants. Please remove some participants first or increase the availability.`);
        return; // Don't update the field
      }
    }
    
    // Special handling for start_date - automatically set end_date to start_date + 1 hour
    if (field === 'start_date' && value) {
      const endDate = addHoursToDateTimeLocal(value, 1);
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        end_date: endDate
      }));
      
      // Clear any existing date validation error
      setDateValidationError('');
      return;
    }
    
    // For end_date or any other field, validate dates after setting
    setFormData(prev => {
      const newFormData = { ...prev, [field]: value };
      
      // Validate dates if we're changing date fields
      if ((field === 'start_date' || field === 'end_date') && newFormData.start_date && newFormData.end_date) {
        const startDate = new Date(newFormData.start_date);
        const endDate = new Date(newFormData.end_date);
        
        if (startDate >= endDate) {
          setDateValidationError('Start date must be before end date');
        } else {
          setDateValidationError('');
        }
      }
      
      return newFormData;
    });
  };

  const getQualificationDisplayName = (qual) => {
    const qualName = qual.qualification_type?.qualification_type_data?.name || 'Unknown';
    const centerName = qual.training_center?.name || 'Unknown';
    const trainingNumber = qual.qualification_type?.training_number || '';
    const price = qual.price ? ` - ${qual.price}` : '';
    return `${qualName} - ${centerName}${trainingNumber ? ` (${trainingNumber})` : ''}${price}`;
  };

  const filteredQualifications = trainingCenterQualifications.filter(qual => {
    const trainingNumber = qual.qualification_type?.training_number || '';
    
    // Exact match filter for training_number_filter field
    if (formData.training_number_filter) {
      if (trainingNumber !== formData.training_number_filter) {
        return false;
      }
    }
    
    // Text search filter for qualification dropdown - search in all displayed elements
    if (qualificationFilter) {
      const searchTerm = qualificationFilter.toLowerCase();
      
      // Get all searchable fields
      const qualName = (qual.qualification_type?.qualification_type_data?.name || '').toLowerCase();
      const centerName = (qual.training_center?.name || '').toLowerCase();
      const trainingNumberLower = trainingNumber.toLowerCase();
      const price = (qual.price ? qual.price.toString() : '').toLowerCase();
      
      // Search in any of these fields
      return qualName.includes(searchTerm) ||
             centerName.includes(searchTerm) ||
             trainingNumberLower.includes(searchTerm) ||
             price.includes(searchTerm);
    }
    
    return true;
  });

  const selectedQualification = trainingCenterQualifications.find(
    qual => qual.id === parseInt(formData.training_center_qualification_id) || qual.id.toString() === formData.training_center_qualification_id.toString()
  );

  const handleQualificationSelect = (qual) => {
    setFormData(prev => ({ 
      ...prev, 
      training_center_qualification_id: qual.id.toString(),
      description: qual.description || '',
      required_items: qual.required_items || ''
    }));
    setQualificationFilter(getQualificationDisplayName(qual));
    setShowQualificationDropdown(false);
  };

  const handleQualificationFilterChange = (value) => {
    setQualificationFilter(value);
    setShowQualificationDropdown(true);
    if (!value) {
      setFormData(prev => ({ ...prev, training_center_qualification_id: '' }));
    }
  };

  const handleTrainingNumberFilterChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      training_number_filter: value,
      training_center_qualification_id: '' // Reset selected qualification when filter changes
    }));
    setQualificationFilter(''); // Reset qualification search text
  };

  const handleClearQualification = () => {
    setFormData(prev => ({ 
      ...prev, 
      training_center_qualification_id: '',
      description: '',
      required_items: ''
    }));
    setQualificationFilter('');
    setShowQualificationDropdown(false);
  };

  // Get required qualification type ID from selected training center qualification
  const getRequiredQualificationTypeId = () => {
    const selectedQual = selectedQualification;
    return selectedQual?.qualification_type?.qualification_type_data?.id;
  };

  // Check user qualification status
  const getUserQualificationStatus = (user) => {
    const requiredQualificationId = getRequiredQualificationTypeId();
    console.log('Checking qualification for user:', user.username, 'Required qualification ID:', requiredQualificationId);
    
    if (!requiredQualificationId || !user.qualifications) {
      return { status: 'missing', color: 'red', daysLeft: null };
    }

    // Find matching qualification - comparing qualification_type (which is an ID) with requiredQualificationId
    const userQualification = user.qualifications.find(
      qual => qual.qualification_type === requiredQualificationId
    );
    
    console.log('User qualifications:', user.qualifications, 'Found qualification:', userQualification);

    if (!userQualification || !userQualification.expiry_date) {
      return { status: 'missing', color: 'red', daysLeft: null };
    }

    // Calculate days until expiration
    const validUntil = new Date(userQualification.expiry_date);
    const today = new Date();
    const daysLeft = Math.ceil((validUntil - today) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) {
      return { status: 'expired', color: 'red', daysLeft };
    } else if (daysLeft <= 30) {
      return { status: 'expiring_soon', color: 'orange', daysLeft };
    } else if (daysLeft <= 60) {
      return { status: 'expiring_medium', color: 'yellow', daysLeft };
    } else {
      return { status: 'valid', color: 'green', daysLeft };
    }
  };

  // Get user display name
  const getUserDisplayName = (user) => {
    return `${user.first_name} ${user.last_name} (${user.username})`;
  };

  // Sort and filter users
  const getSortedAndFilteredUsers = () => {
    let filteredUsers = users.filter(user => {
      // Filter by name and role
      const displayName = getUserDisplayName(user);
      const roleName = user.training_role_data?.name || '';
      const nameMatch = displayName.toLowerCase().includes(userFilter.toLowerCase()) ||
                       roleName.toLowerCase().includes(userFilter.toLowerCase());
      
      // Filter by training role
      const roleMatch = filters.trainingRoles.length === 0 || 
        filters.trainingRoles.includes(user.training_role);
      
      // Filter by training site
      const siteMatch = filters.trainingSites.length === 0 || 
        filters.trainingSites.includes(user.training_site);
      
      // Filter by training country
      const countryMatch = filters.trainingCountries.length === 0 || 
        filters.trainingCountries.includes(user.training_country);
      
      // Filter by manager
      const managerMatch = filters.managers.length === 0 || 
        filters.managers.includes(user.manager);
      
      // Filter by supervisor
      const supervisorMatch = filters.supervisors.length === 0 || 
        filters.supervisors.includes(user.training_supervisor);
      
      // Filter by coordinator
      const coordinatorMatch = filters.coordinators.length === 0 || 
        filters.coordinators.includes(user.training_coordinator);
      
      // Filter by qualification status
      let statusMatch = true;
      if (filters.qualificationStatus.length > 0) {
        const qualStatus = getUserQualificationStatus(user);
        // Map expired status to missing for filter purposes
        let filterStatus = qualStatus.status;
        if (qualStatus.status === 'expired') {
          filterStatus = 'missing';
        }
        statusMatch = filters.qualificationStatus.includes(filterStatus);
      }
      
      return nameMatch && roleMatch && siteMatch && countryMatch && managerMatch && supervisorMatch && coordinatorMatch && statusMatch;
    });

    // Sort by qualification status (red first, then orange, yellow, green)
    const statusOrder = { 'red': 1, 'orange': 2, 'yellow': 3, 'green': 4 };
    
    filteredUsers.sort((a, b) => {
      const statusA = getUserQualificationStatus(a);
      const statusB = getUserQualificationStatus(b);
      
      // First sort by color priority
      const colorComparison = statusOrder[statusA.color] - statusOrder[statusB.color];
      if (colorComparison !== 0) return colorComparison;
      
      // If same color and not red (missing/expired), sort by days left (ascending)
      if (statusA.color === statusB.color && statusA.color !== 'red') {
        if (statusA.daysLeft !== null && statusB.daysLeft !== null) {
          return statusA.daysLeft - statusB.daysLeft;
        }
      }
      
      // Finally sort by name
      return getUserDisplayName(a).localeCompare(getUserDisplayName(b));
    });

    return filteredUsers;
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    const maxUsers = formData.availability ? parseInt(formData.availability) : null;
    const isSelected = selectedUsers.some(u => u.id === user.id);
    const canSelect = !maxUsers || selectedUsers.length < maxUsers || isSelected;
    
    if (isSelected) {
      // Remove user if already selected
      setSelectedUsers(prev => prev.filter(u => u.id !== user.id));
    } else if (canSelect) {
      // Add user if within limit
      setSelectedUsers(prev => [...prev, user]);
    } else {
      // Show warning when trying to add user beyond limit
      toast.warning("You have already reached the limit of available spots for this training");
    }
  };

  // Remove user from selected list
  const handleRemoveUser = (userId) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  };


  // Get selected users display text
  const getSelectedUsersText = () => {
    if (selectedUsers.length === 0) return 'Select users...';
    
    const selectedUserObjects = users.filter(user => selectedUsers.some(u => u.id === user.id));
    if (selectedUserObjects.length === 1) {
      return getUserDisplayName(selectedUserObjects[0]);
    }
    return `${selectedUserObjects.length} users selected`;
  };

  // Get color classes for user status
  const getColorClasses = (color) => {
    switch (color) {
      case 'green':
        return 'text-green-700 bg-green-50 hover:bg-green-100';
      case 'yellow':
        return 'text-yellow-700 bg-yellow-50 hover:bg-yellow-100';
      case 'orange':
        return 'text-orange-700 bg-orange-50 hover:bg-orange-100';
      case 'red':
        return 'text-red-700 bg-red-50 hover:bg-red-100';
      default:
        return 'text-slate-700 bg-steel-50 hover:bg-brand-50';
    }
  };

  // Detect changes in key fields for approved trainings
  const detectTrainingChanges = () => {
    if (!isEditing || !originalData || trainingData?.status !== 'approved') {
      return null;
    }

    const changes = [];

    // Check Training Center Qualification change
    const currentQualId = formData.training_center_qualification_id ? formData.training_center_qualification_id.toString() : '';
    const originalQualId = originalData.training_center_qualification_id ? originalData.training_center_qualification_id.toString() : '';
    
    if (currentQualId !== originalQualId) {
      const oldQual = originalQualId ? trainingCenterQualifications.find(q => q.id.toString() === originalQualId) : null;
      const newQual = currentQualId ? trainingCenterQualifications.find(q => q.id.toString() === currentQualId) : null;
      
      changes.push({
        field: 'training_center_qualification',
        label: 'Training Center Qualification',
        oldValue: oldQual ? getQualificationDisplayName(oldQual) : 'Not set',
        newValue: newQual ? getQualificationDisplayName(newQual) : 'Not set'
      });
    }

    // Check start date change
    if (formData.start_date !== originalData.start_date) {
      changes.push({
        field: 'start_date',
        label: 'Start Date',
        oldValue: formatDateTimeForDisplay(originalData.start_date),
        newValue: formatDateTimeForDisplay(formData.start_date)
      });
    }

    // Check end date change
    if (formData.end_date !== originalData.end_date) {
      changes.push({
        field: 'end_date',
        label: 'End Date',
        oldValue: formatDateTimeForDisplay(originalData.end_date),
        newValue: formatDateTimeForDisplay(formData.end_date)
      });
    }

    // Check description change
    if (formData.description !== originalData.description) {
      changes.push({
        field: 'description',
        label: 'Description',
        oldValue: originalData.description || 'Not set',
        newValue: formData.description || 'Not set'
      });
    }

    // Check required items change
    if (formData.required_items !== originalData.required_items) {
      changes.push({
        field: 'required_items',
        label: 'Required Items',
        oldValue: originalData.required_items || 'Not set',
        newValue: formData.required_items || 'Not set'
      });
    }

    return changes.length > 0 ? changes : null;
  };

  // Detect participant changes for approved trainings
  const detectParticipantChanges = () => {
    if (!isEditing || !originalData || trainingData?.status !== 'approved') {
      return null;
    }

    const originalParticipants = originalData.participants || [];
    const currentParticipants = selectedUsers.map(u => u.id);

    // Find added participants
    const addedParticipants = currentParticipants.filter(id => !originalParticipants.includes(id));
    const addedUsers = selectedUsers.filter(u => addedParticipants.includes(u.id));

    // Find removed participants
    const removedParticipants = originalParticipants.filter(id => !currentParticipants.includes(id));
    // We need to get user details for removed participants from original training data
    const removedUsers = trainingData.users_data?.filter(u => removedParticipants.includes(u.id)) || [];

    return {
      added: addedUsers,
      removed: removedUsers,
      hasChanges: addedUsers.length > 0 || removedUsers.length > 0
    };
  };

  const handleSubmit = async () => {
    
    if (!formData.training_center_qualification_id) {
      toast.error("Please select a training center qualification");
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      toast.error("Please set start and end dates");
      return;
    }
    
    // Validate date logic
    if (dateValidationError) {
      toast.error(dateValidationError);
      return;
    }
    
    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);
    
    if (startDate >= endDate) {
      toast.error("Start date must be before end date");
      return;
    }

    // Additional validation
    const qualificationId = parseInt(formData.training_center_qualification_id);
    if (isNaN(qualificationId)) {
      toast.error("Invalid training center qualification selected");
      console.error('NaN qualification ID:', formData.training_center_qualification_id);
      return;
    }

    if (!user?.company) {
      toast.error("User company not found");
      console.error('Missing user company:', user);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      
      // Create properly formatted data for backend
      const submitData = {
        name: formData.name,
        training_center_qualification_id: qualificationId,
        training_center_qualification: qualificationId,
        start_date: formatDateTimeForBackend(formData.start_date),
        end_date: formatDateTimeForBackend(formData.end_date),
        description: formData.description,
        required_items: formData.required_items,
        availability: formData.availability ? parseInt(formData.availability) : null,
        status: formData.status,
        company: user.company,
        users: selectedUsers.map(u => u.id) // Array of user IDs - matches Django model field name
      };

      console.log('Submitting training data:', submitData);
      console.log('Selected users for training:', selectedUsers);
      console.log('User context:', user);

      if (isEditing) {
        // Detect changes for approved trainings before updating
        const detectedChanges = detectTrainingChanges();
        const participantChanges = detectParticipantChanges();
        const hasParticipants = selectedUsers.length > 0;
        const shouldOpenMailSender = (detectedChanges || participantChanges?.hasChanges) && trainingData?.status === 'approved' && hasParticipants;


        // Update existing training
        await axios.put(
          `${API_BASE_URL}/trainings/${trainingData.id}/`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Training updated successfully");

        // Provide feedback when changes detected but no participants to notify
        if ((detectedChanges || participantChanges?.hasChanges) && trainingData?.status === 'approved' && !hasParticipants) {
          console.log('Changes detected in approved training, but no participants to notify');
          toast.info("Training updated successfully. No email notifications sent as this training has no participants.");
        }

        // Check if we should open TrainingMailSender for changes in approved training
        if (shouldOpenMailSender) {
          console.log('Changes detected in approved training, opening mail sender');
          
          // Get the updated training data with user details
          const trainingResponse = await axios.get(
            `${API_BASE_URL}/trainings/${trainingData.id}/`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          // Determine the type of change
          let changeType;
          if (participantChanges?.hasChanges && !detectedChanges) {
            // Only participant changes - use participant_changes mode
            changeType = 'participant_changes';
          } else if (detectedChanges && !participantChanges?.hasChanges) {
            // Only field changes - use changes mode  
            changeType = 'changes';
          } else {
            // Both types of changes - use changes mode (existing logic)
            changeType = 'changes';
          }

          setMailSenderData({
            training: trainingResponse.data,
            statusChange: {
              type: changeType,
              changes: detectedChanges,
              participantChanges: participantChanges
            }
          });
          
          // Don't close the modal yet - let user handle email sending first
          return;
        }
      } else {
        // Create new training
        const response = await axios.post(
          `${API_BASE_URL}/trainings/`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Training created successfully");
        
        // Check if we should open TrainingMailSender for new training
        // Conditions: new training with approved status and participants
        if (formData.status === 'approved' && selectedUsers.length > 0) {
          console.log('New training with approved status and participants, opening mail sender');
          
          // Get the created training data with user details
          const createdTrainingId = response.data.id;
          const trainingResponse = await axios.get(
            `${API_BASE_URL}/trainings/${createdTrainingId}/`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          setMailSenderData({
            training: trainingResponse.data,
            statusChange: {
              from: 'planned',
              to: 'approved'
            }
          });
          
          // Don't close the modal yet - let user handle email sending first
          return;
        }
      }

      fetchData();
      
      // Don't close modal if it's part of multiple trainings sequence
      if (!trainingData?.isMultipleTrainings) {
        onClose();
      }
    } catch (error) {
      console.error("Error saving training:", error);
      console.error("Response data:", error.response?.data);
      console.error("Response status:", error.response?.status);
      console.error("Response headers:", error.response?.headers);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          const errorMessages = Object.entries(errorData)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          toast.error(`Error: ${errorMessages}`);
        } else {
          toast.error(`Error: ${errorData}`);
        }
      } else {
        toast.error("Failed to save training");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditing) return;

    if (!window.confirm('Are you sure you want to delete this training?')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      
      await axios.delete(
        `${API_BASE_URL}/trainings/${trainingData.id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Training deleted successfully");
      fetchData();
      onClose();
    } catch (error) {
      console.error("Error deleting training:", error);
      toast.error("Failed to delete training");
    } finally {
      setLoading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showQualificationDropdown && !event.target.closest('.qualification-dropdown-container')) {
        setShowQualificationDropdown(false);
      }
      if (showUserDropdown && !event.target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
      
      // Close filter dropdowns when clicking outside
      if (dropdownStates.qualificationStatus && !event.target.closest('[data-filter="qualificationStatus"]')) {
        setDropdownStates(prev => ({ ...prev, qualificationStatus: false }));
      }
      if (dropdownStates.roles && !event.target.closest('[data-filter="roles"]')) {
        setDropdownStates(prev => ({ ...prev, roles: false }));
      }
      if (dropdownStates.sites && !event.target.closest('[data-filter="sites"]')) {
        setDropdownStates(prev => ({ ...prev, sites: false }));
      }
      if (dropdownStates.countries && !event.target.closest('[data-filter="countries"]')) {
        setDropdownStates(prev => ({ ...prev, countries: false }));
      }
      if (dropdownStates.managers && !event.target.closest('[data-filter="managers"]')) {
        setDropdownStates(prev => ({ ...prev, managers: false }));
      }
      if (dropdownStates.supervisors && !event.target.closest('[data-filter="supervisors"]')) {
        setDropdownStates(prev => ({ ...prev, supervisors: false }));
      }
      if (dropdownStates.coordinators && !event.target.closest('[data-filter="coordinators"]')) {
        setDropdownStates(prev => ({ ...prev, coordinators: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQualificationDropdown, showUserDropdown, dropdownStates]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${showUserPanel && showFiltersPanel ? 'sm:max-w-[1800px]' : showUserPanel ? 'sm:max-w-[1200px]' : 'sm:max-w-[600px]'} max-h-[95vh] overflow-y-auto`}>
        <DialogHeader>
          <div className={`${showUserPanel ? 'flex gap-6 items-center' : ''}`}>
            <div className={`${showUserPanel ? 'flex-1' : ''}`}>
              <DialogTitle>
                {isEditing ? "Edit Training" : "Create New Training"}
                {trainingData?.isMultipleTrainings && (
                  <span className="text-sm font-normal text-brand-500 ml-2">
                    (Training {trainingData.currentTrainingIndex} of {trainingData.totalTrainings})
                  </span>
                )}
              </DialogTitle>
            </div>
            {showUserPanel && (
              <div className="flex-1">
                <h3 className="text-lg font-medium">Manage Users</h3>
              </div>
            )}
            {showFiltersPanel && (
              <div className="flex-1">
                <h3 className="text-lg font-medium">Filters</h3>
              </div>
            )}
          </div>
        </DialogHeader>
        
        <div className={`${showUserPanel ? 'flex gap-6' : ''}`}>
          {/* Main Training Form */}
          <div className={`${showUserPanel ? 'flex-1' : ''} space-y-4`}>
          {/* Training Name and Training Number Filter */}
          <div className="grid grid-cols-5 gap-4">
            <div className="col-span-4 space-y-2">
              <label className="text-sm font-medium">Training Name</label>
              <input
                type="text"
                placeholder="Custom name for this training session"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            <div className="col-span-1 space-y-2">
              <label className="text-sm font-medium">Training No</label>
              <input
                type="text"
                placeholder="Number"
                value={formData.training_number_filter}
                onChange={(e) => handleTrainingNumberFilterChange(e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
          </div>

          {/* Training Center Qualification */}
          <div className="space-y-2 relative qualification-dropdown-container">
            <label className="text-sm font-medium">Training Center Qualification *</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Type to search training center qualifications..."
                value={qualificationFilter}
                onChange={(e) => handleQualificationFilterChange(e.target.value)}
                onFocus={() => setShowQualificationDropdown(true)}
                className="w-full p-2 pr-8 border rounded"
                required
              />
              {formData.training_center_qualification_id && (
                <button
                  type="button"
                  onClick={handleClearQualification}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700 font-bold text-lg"
                  title="Clear selection"
                >
                  ×
                </button>
              )}
            </div>
            {showQualificationDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {filteredQualifications.length > 0 ? (
                  filteredQualifications.map((qual) => (
                    <div
                      key={qual.id}
                      onClick={() => handleQualificationSelect(qual)}
                      className="p-2 hover:bg-brand-50 cursor-pointer border-b last:border-b-0"
                    >
                      {getQualificationDisplayName(qual)}
                    </div>
                  ))
                ) : (
                  <div className="p-2 text-slate-500">No qualifications found</div>
                )}
              </div>
            )}
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Start Date & Time *</label>
              <input
                type="datetime-local"
                value={formData.start_date}
                onChange={(e) => handleInputChange("start_date", e.target.value)}
                className={`w-full p-2 border rounded ${dateValidationError ? 'border-red-500' : ''}`}
                required
              />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">End Date & Time *</label>
              <input
                type="datetime-local"
                value={formData.end_date}
                onChange={(e) => handleInputChange("end_date", e.target.value)}
                className={`w-full p-2 border rounded ${dateValidationError ? 'border-red-500' : ''}`}
                required
              />
            </div>
          </div>
          
          {/* Date Validation Error */}
          {dateValidationError && (
            <div className="text-red-500 text-sm flex items-center">
              <span className="mr-1">⚠️</span>
              {dateValidationError}
            </div>
          )}

          {/* Availability and Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Available Spots</label>
              <input
                type="number"
                placeholder="Maximum number of participants"
                value={formData.availability}
                onChange={(e) => handleInputChange("availability", e.target.value)}
                className="w-full p-2 border rounded"
                min="1"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="planned">Planned</option>
                <option value="approved">Approved</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <textarea
              placeholder="Additional details about this training"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              className="w-full p-2 border rounded h-20 resize-none"
            />
          </div>

          {/* Required Items */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Required Items</label>
            <textarea
              placeholder="Items participants need to bring"
              value={formData.required_items}
              onChange={(e) => handleInputChange("required_items", e.target.value)}
              className="w-full p-2 border rounded h-20 resize-none"
            />
          </div>
          </div>

          {/* User Management Panel */}
          {showUserPanel && (
            <div className="flex-1 border-l border-steel-200 pl-6">
              <div className="space-y-3">
                {/* Info and Filters Button */}
                <div className="flex justify-between items-center gap-4">
                  <div className="text-sm text-slate-600">
                    Available Spots: {formData.availability || 'Unlimited'} | Selected: {selectedUsers.length}
                  </div>
                  <div className="flex-shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowFiltersPanel(!showFiltersPanel)}
                      className="text-xs px-3 py-1 h-8"
                    >
                      Filters {showFiltersPanel ? '▼' : '▲'}
                    </Button>
                  </div>
                </div>
                
                {/* User Multiselect */}
                <div className="space-y-2 relative user-dropdown-container">
                  <label className="text-sm font-medium">Select Users</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={userFilter}
                      onChange={(e) => setUserFilter(e.target.value)}
                      onFocus={() => setShowUserDropdown(true)}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  
                  {showUserDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-[480px] overflow-y-auto">
                      {getSortedAndFilteredUsers().length > 0 ? (
                        getSortedAndFilteredUsers().map((user) => {
                          const qualStatus = getUserQualificationStatus(user);
                          const isSelected = selectedUsers.some(u => u.id === user.id);
                          const maxUsers = formData.availability ? parseInt(formData.availability) : null;
                          const canSelect = !maxUsers || selectedUsers.length < maxUsers || isSelected;
                          
                          return (
                            <div
                              key={user.id}
                              onClick={() => handleUserSelect(user)}
                              className={`p-3 cursor-pointer border-b last:border-b-0 ${
                                getColorClasses(qualStatus.color)
                              } ${isSelected ? 'ring-2 ring-brand-500' : ''} ${
                                !canSelect ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">{getUserDisplayName(user)}</div>
                                  <div className="text-xs flex gap-2">
                                    <span className="text-slate-600">
                                      {user.training_role_data?.name || 'No role'}
                                    </span>
                                    <span className="text-slate-400">•</span>
                                    <span>
                                      {qualStatus.status === 'missing' && 'No qualification'}
                                      {qualStatus.status === 'expired' && 'Qualification expired'}
                                      {qualStatus.status === 'expiring_soon' && `Expires in ${qualStatus.daysLeft} days`}
                                      {qualStatus.status === 'expiring_medium' && `Expires in ${qualStatus.daysLeft} days`}
                                      {qualStatus.status === 'valid' && `Valid for ${qualStatus.daysLeft} days`}
                                    </span>
                                  </div>
                                </div>
                                <div className="text-xs">
                                  {isSelected && '✓'}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="p-3 text-slate-500">No users found</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected Users List */}
                {selectedUsers.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Selected Participants ({selectedUsers.length})</h4>
                    <div className="space-y-2 max-h-[480px] overflow-y-auto">
                      {selectedUsers.map((user) => {
                        const qualStatus = getUserQualificationStatus(user);
                        
                        return (
                          <div
                            key={user.id}
                            className={`p-2 rounded border ${getColorClasses(qualStatus.color)} flex justify-between items-center`}
                          >
                            <div className="flex-1">
                              <div className="font-medium text-sm">{getUserDisplayName(user)}</div>
                              <div className="text-xs flex gap-2">
                                <span className="text-slate-600">
                                  {user.training_role_data?.name || 'No role'}
                                </span>
                                <span className="text-slate-400">•</span>
                                <span>
                                  {qualStatus.status === 'missing' && 'No qualification'}
                                  {qualStatus.status === 'expired' && 'Qualification expired'}
                                  {qualStatus.status === 'expiring_soon' && `Expires in ${qualStatus.daysLeft} days`}
                                  {qualStatus.status === 'expiring_medium' && `Expires in ${qualStatus.daysLeft} days`}
                                  {qualStatus.status === 'valid' && `Valid for ${qualStatus.daysLeft} days`}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveUser(user.id)}
                              className="text-red-500 hover:text-red-700 font-bold text-sm ml-2 p-1"
                              title="Remove user"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Filters Panel - Same as MainboardPanel */}
          {showFiltersPanel && (
            <div className="flex-1 border-l border-steel-200 pl-6">
              <div className="space-y-4">
                {/* Clear All Filters */}
                <div className="mb-6">
                  <button
                    onClick={() => {
                      setFilters({
                        trainingRoles: [],
                        trainingSites: [],
                        trainingCountries: [],
                        managers: [],
                        supervisors: [],
                        coordinators: [],
                        qualificationStatus: []
                      });
                      setSearchTerms({
                        roles: '',
                        sites: '',
                        countries: '',
                        managers: '',
                        supervisors: '',
                        coordinators: ''
                      });
                      setDropdownStates({
                        roles: false,
                        sites: false,
                        countries: false,
                        managers: false,
                        supervisors: false,
                        coordinators: false,
                        qualificationStatus: false
                      });
                    }}
                    className="text-sm text-brand-500 hover:text-brand-600 font-medium"
                  >
                    Clear All Filters
                  </button>
                </div>

                {/* Filters Grid - Two Columns */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Qualification Status Filter */}
                  <div className="mb-6" data-filter="qualificationStatus">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Qualification Status</h3>
                    <div className="relative">
                      <button
                        onClick={() => setDropdownStates(prev => ({ ...prev, qualificationStatus: !prev.qualificationStatus }))}
                        className="w-full px-3 py-2 text-left text-sm border border-steel-300 rounded-xl hover:bg-steel-50 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <div className="flex items-center justify-between">
                          <span>
                            {filters.qualificationStatus.length === 0
                              ? 'All statuses'
                              : `${filters.qualificationStatus.length} selected`}
                          </span>
                          <ChevronDown className="w-4 h-4" />
                        </div>
                      </button>
                      {dropdownStates.qualificationStatus && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          <div className="max-h-48 overflow-y-auto">
                            <label className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.qualificationStatus.includes('missing')}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFilters(prev => ({ ...prev, qualificationStatus: [...prev.qualificationStatus, 'missing'] }));
                                  } else {
                                    setFilters(prev => ({ ...prev, qualificationStatus: prev.qualificationStatus.filter(s => s !== 'missing') }));
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className="text-sm">Missing or Expired</span>
                            </label>
                            <label className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.qualificationStatus.includes('expiring_soon')}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFilters(prev => ({ ...prev, qualificationStatus: [...prev.qualificationStatus, 'expiring_soon'] }));
                                  } else {
                                    setFilters(prev => ({ ...prev, qualificationStatus: prev.qualificationStatus.filter(s => s !== 'expiring_soon') }));
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className="text-sm">Expiring Soon (≤30 days)</span>
                            </label>
                            <label className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.qualificationStatus.includes('expiring_medium')}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFilters(prev => ({ ...prev, qualificationStatus: [...prev.qualificationStatus, 'expiring_medium'] }));
                                  } else {
                                    setFilters(prev => ({ ...prev, qualificationStatus: prev.qualificationStatus.filter(s => s !== 'expiring_medium') }));
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className="text-sm">Expiring (31-60 days)</span>
                            </label>
                            <label className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.qualificationStatus.includes('valid')}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFilters(prev => ({ ...prev, qualificationStatus: [...prev.qualificationStatus, 'valid'] }));
                                  } else {
                                    setFilters(prev => ({ ...prev, qualificationStatus: prev.qualificationStatus.filter(s => s !== 'valid') }));
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className="text-sm">Valid (over 60 days)</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Roles Filter */}
                  <div className="mb-6" data-filter="roles">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Training Roles</h3>
                    <div className="relative">
                      <button
                        onClick={() => setDropdownStates(prev => ({ ...prev, roles: !prev.roles }))}
                        className="w-full px-3 py-2 text-left text-sm border border-steel-300 rounded-xl hover:bg-steel-50 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <div className="flex items-center justify-between">
                          <span>
                            {filters.trainingRoles.length === 0
                              ? 'All roles'
                              : `${filters.trainingRoles.length} selected`}
                          </span>
                          <ChevronDown className="w-4 h-4" />
                        </div>
                      </button>
                      {dropdownStates.roles && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          <div className="p-2">
                            <input
                              type="text"
                              placeholder="Search roles..."
                              value={searchTerms.roles}
                              onChange={(e) => setSearchTerms(prev => ({ ...prev, roles: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {trainingRoles
                              .filter(role => role.name.toLowerCase().includes(searchTerms.roles.toLowerCase()))
                              .map(role => (
                                <label key={role.id} className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={filters.trainingRoles.includes(role.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFilters(prev => ({ ...prev, trainingRoles: [...prev.trainingRoles, role.id] }));
                                      } else {
                                        setFilters(prev => ({ ...prev, trainingRoles: prev.trainingRoles.filter(id => id !== role.id) }));
                                      }
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">{role.name}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sites Filter */}
                  <div className="mb-6" data-filter="sites">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Training Sites</h3>
                    <div className="relative">
                      <button
                        onClick={() => setDropdownStates(prev => ({ ...prev, sites: !prev.sites }))}
                        className="w-full px-3 py-2 text-left text-sm border border-steel-300 rounded-xl hover:bg-steel-50 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <div className="flex items-center justify-between">
                          <span>
                            {filters.trainingSites.length === 0
                              ? 'All sites'
                              : `${filters.trainingSites.length} selected`}
                          </span>
                          <ChevronDown className="w-4 h-4" />
                        </div>
                      </button>
                      {dropdownStates.sites && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          <div className="p-2">
                            <input
                              type="text"
                              placeholder="Search sites..."
                              value={searchTerms.sites}
                              onChange={(e) => setSearchTerms(prev => ({ ...prev, sites: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {trainingSites
                              .filter(site => site.name.toLowerCase().includes(searchTerms.sites.toLowerCase()))
                              .map(site => (
                                <label key={site.id} className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={filters.trainingSites.includes(site.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFilters(prev => ({ ...prev, trainingSites: [...prev.trainingSites, site.id] }));
                                      } else {
                                        setFilters(prev => ({ ...prev, trainingSites: prev.trainingSites.filter(id => id !== site.id) }));
                                      }
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">{site.name}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Countries Filter */}
                  <div className="mb-6" data-filter="countries">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Training Countries</h3>
                    <div className="relative">
                      <button
                        onClick={() => setDropdownStates(prev => ({ ...prev, countries: !prev.countries }))}
                        className="w-full px-3 py-2 text-left text-sm border border-steel-300 rounded-xl hover:bg-steel-50 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <div className="flex items-center justify-between">
                          <span>
                            {filters.trainingCountries.length === 0
                              ? 'All countries'
                              : `${filters.trainingCountries.length} selected`}
                          </span>
                          <ChevronDown className="w-4 h-4" />
                        </div>
                      </button>
                      {dropdownStates.countries && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          <div className="p-2">
                            <input
                              type="text"
                              placeholder="Search countries..."
                              value={searchTerms.countries}
                              onChange={(e) => setSearchTerms(prev => ({ ...prev, countries: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {trainingCountries
                              .filter(country => country.name.toLowerCase().includes(searchTerms.countries.toLowerCase()))
                              .map(country => (
                                <label key={country.id} className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={filters.trainingCountries.includes(country.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFilters(prev => ({ ...prev, trainingCountries: [...prev.trainingCountries, country.id] }));
                                      } else {
                                        setFilters(prev => ({ ...prev, trainingCountries: prev.trainingCountries.filter(id => id !== country.id) }));
                                      }
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">{country.name}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Managers Filter */}
                  <div className="mb-6" data-filter="managers">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Managers</h3>
                    <div className="relative">
                      <button
                        onClick={() => setDropdownStates(prev => ({ ...prev, managers: !prev.managers }))}
                        className="w-full px-3 py-2 text-left text-sm border border-steel-300 rounded-xl hover:bg-steel-50 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <div className="flex items-center justify-between">
                          <span>
                            {filters.managers.length === 0
                              ? 'All managers'
                              : `${filters.managers.length} selected`}
                          </span>
                          <ChevronDown className="w-4 h-4" />
                        </div>
                      </button>
                      {dropdownStates.managers && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          <div className="p-2">
                            <input
                              type="text"
                              placeholder="Search managers..."
                              value={searchTerms.managers}
                              onChange={(e) => setSearchTerms(prev => ({ ...prev, managers: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {managers
                              .filter(manager => `${manager.first_name} ${manager.last_name}`.toLowerCase().includes(searchTerms.managers.toLowerCase()))
                              .map(manager => (
                                <label key={manager.id} className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={filters.managers.includes(manager.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFilters(prev => ({ ...prev, managers: [...prev.managers, manager.id] }));
                                      } else {
                                        setFilters(prev => ({ ...prev, managers: prev.managers.filter(id => id !== manager.id) }));
                                      }
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">{manager.first_name} {manager.last_name}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Supervisors Filter */}
                  <div className="mb-6" data-filter="supervisors">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Training Supervisors</h3>
                    <div className="relative">
                      <button
                        onClick={() => setDropdownStates(prev => ({ ...prev, supervisors: !prev.supervisors }))}
                        className="w-full px-3 py-2 text-left text-sm border border-steel-300 rounded-xl hover:bg-steel-50 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <div className="flex items-center justify-between">
                          <span>
                            {filters.supervisors.length === 0
                              ? 'All supervisors'
                              : `${filters.supervisors.length} selected`}
                          </span>
                          <ChevronDown className="w-4 h-4" />
                        </div>
                      </button>
                      {dropdownStates.supervisors && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          <div className="p-2">
                            <input
                              type="text"
                              placeholder="Search supervisors..."
                              value={searchTerms.supervisors}
                              onChange={(e) => setSearchTerms(prev => ({ ...prev, supervisors: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {supervisors
                              .filter(supervisor => `${supervisor.first_name} ${supervisor.last_name}`.toLowerCase().includes(searchTerms.supervisors.toLowerCase()))
                              .map(supervisor => (
                                <label key={supervisor.id} className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={filters.supervisors.includes(supervisor.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFilters(prev => ({ ...prev, supervisors: [...prev.supervisors, supervisor.id] }));
                                      } else {
                                        setFilters(prev => ({ ...prev, supervisors: prev.supervisors.filter(id => id !== supervisor.id) }));
                                      }
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">{supervisor.first_name} {supervisor.last_name}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Coordinators Filter */}
                  <div className="mb-6" data-filter="coordinators">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Training Coordinators</h3>
                    <div className="relative">
                      <button
                        onClick={() => setDropdownStates(prev => ({ ...prev, coordinators: !prev.coordinators }))}
                        className="w-full px-3 py-2 text-left text-sm border border-steel-300 rounded-xl hover:bg-steel-50 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <div className="flex items-center justify-between">
                          <span>
                            {filters.coordinators.length === 0
                              ? 'All coordinators'
                              : `${filters.coordinators.length} selected`}
                          </span>
                          <ChevronDown className="w-4 h-4" />
                        </div>
                      </button>
                      {dropdownStates.coordinators && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          <div className="p-2">
                            <input
                              type="text"
                              placeholder="Search coordinators..."
                              value={searchTerms.coordinators}
                              onChange={(e) => setSearchTerms(prev => ({ ...prev, coordinators: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {coordinators
                              .filter(coordinator => `${coordinator.first_name} ${coordinator.last_name}`.toLowerCase().includes(searchTerms.coordinators.toLowerCase()))
                              .map(coordinator => (
                                <label key={coordinator.id} className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={filters.coordinators.includes(coordinator.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFilters(prev => ({ ...prev, coordinators: [...prev.coordinators, coordinator.id] }));
                                      } else {
                                        setFilters(prev => ({ ...prev, coordinators: prev.coordinators.filter(id => id !== coordinator.id) }));
                                      }
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">{coordinator.first_name} {coordinator.last_name}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div>
            {isEditing && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={loading}
                className="mr-2"
              >
                Delete
              </Button>
            )}
          </div>
          <div className="space-x-2">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            {/* Only show Hide Users button if no participants are selected */}
            {(!showUserPanel || selectedUsers.length === 0) && (
              <Button 
                variant="secondary" 
                onClick={() => {
                  if (!showUserPanel) {
                    fetchUsers();
                  } else {
                    // When hiding users panel, also hide filters panel
                    setShowFiltersPanel(false);
                  }
                  setShowUserPanel(!showUserPanel);
                }}
                disabled={loading}
              >
                {showUserPanel ? "Hide Users" : "Add Users"}
              </Button>
            )}
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : isEditing ? "Update" : "Create"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
      
      {/* TrainingMailSender Modal - Opens after creating approved training with participants */}
      {mailSenderData && (
        <TrainingMailSender
          isOpen={!!mailSenderData}
          onClose={() => {
            setMailSenderData(null);
            // Close the main modal after email handling is done
            if (!trainingData?.isMultipleTrainings) {
              onClose();
            }
          }}
          trainingData={mailSenderData.training}
          statusChange={mailSenderData.statusChange}
          onSuccess={(result) => {
            // Show success notification
            const participantsSent = result.results?.participants_sent || 0;
            const supervisorsSent = result.results?.supervisors_sent || 0;
            const trainingCenterSent = result.results?.training_center_sent || 0;
            const totalSent = participantsSent + supervisorsSent + trainingCenterSent;
            
            toast.success(`📧 Emails sent successfully! Participants: ${participantsSent}, Supervisors: ${supervisorsSent}, Training Center: ${trainingCenterSent} (Total: ${totalSent})`);
            
            // Refresh data in parent (MainboardPanel) if available
            if (fetchData) {
              fetchData();
            }
          }}
        />
      )}
    </Dialog>
  );
};

export default TrainingEditModal;